// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unused-vars, prefer-const, react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Select } from "semantic-ui-react";
import * as analysisApi from "../../../../../apis/analysisApi.js";
import { useAuth0 } from "../../../../../auth/auth0/index.js";
import { useModuleNavigation } from "../../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../../hooks/useUserAuth.js";
import { setupAnalysisOptions } from "../../../../Lumber/OrthoViewer/orthoControlFunctions.js";
import { type HeatmapData, useDataDispatch, useGraph, useGraphDispatch, useViewerDispatch } from "../StateManagement";
import type { AnalysisOptionsData } from "../../../../../apis/types/viewerTypes";
import { DateTimeSlider, HeatmapOptions } from "./";
import { GEOMFA_MENU_HEATMAP } from "../../../../../utilities/defaults";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { DateTime } from "luxon";

type Props = {
	minimized: boolean;
};

export const HeatmapDropdown = ({ minimized }: Props) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();

	const { updateHeatmapVisible } = useViewerDispatch();
	const { updateHeatMapData, updateHeatMapDataAllDates, updateHeatMapDataLoading } = useDataDispatch();
	const { updateAxis } = useGraphDispatch();
	const { chartType } = useGraph();

	const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({
		id: "heatmap",
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const [selectedAnalysisId, setSelectedAnalysisId] = useState<string>();
	const [heatmapVisibleLocal, setHeatmapVisibleLocal] = useState(true);
	const [selectedTimeValue, setSelectedTimeValue] = useState(0);
	const prevChartTypeRef = useRef<string>();

	const getHeatmapAnalysisResults = async () => {
		if (selectedAnalysisId && analysisOptionsData) {
			const accessToken = await getTokenSilently();
			let analysisId,
				analysisTypeId,
				analysisTypeName,
				assessmentId,
				quantifiedRegionTypeId,
				curveModelAnalysisId = "";

			let splitSelectedAnalysisIds = selectedAnalysisId.split("/");

			analysisId = _.find(analysisOptionsData.analysisOptions, {
				analysisId: splitSelectedAnalysisIds[0],
			})?.analysisId;
			assessmentId = _.find(analysisOptionsData.groundDataOptions, { assessmentId: selectedAnalysisId })?.assessmentId;

			analysisTypeId = splitSelectedAnalysisIds[1];
			quantifiedRegionTypeId = splitSelectedAnalysisIds[2];
			analysisTypeName = analysisTypeId
				? _.find([...analysisOptionsData.analysisOptions, ...analysisOptionsData.curveModelAnalysisOptions], {
						analysisTypeId: analysisTypeId,
						quantifiedRegionTypeId: quantifiedRegionTypeId,
					})?.analysisTypeName
				: null;
			let curveModelData = _.find(
				analysisOptionsData.curveModelAnalysisOptions,
				(cmao) =>
					cmao.analysisId === selectedAnalysisId.split("/")[0] &&
					cmao.curveModelAnalysisId === selectedAnalysisId.split("/")[2],
			);

			if (curveModelData) {
				analysisId = curveModelData.analysisId;
				curveModelAnalysisId = curveModelData.curveModelAnalysisId;
			}

			const json = analysisApi.getHeatmapAnalysisResultsForTrialAsync(
				userAuth.currentClientId,
				moduleNavigation.trialId,
				analysisId,
				analysisTypeId,
				analysisTypeName,
				assessmentId,
				curveModelAnalysisId,
				accessToken,
			);
			return json;
		}
		return null;
	};

	const getAnalysisOptions = async () => {
		const accessToken = await getTokenSilently();
		const json = analysisApi.getAnalysisOptionsForViewerAsync(
			userAuth.currentClientId,
			moduleNavigation.trialId,
			null,
			accessToken,
		);
		return json;
	};

	const { isFetching: heatmapDataLoading, data: heatmapData } = useQuery({
		queryKey: ["mfa viewer heatmap data", userAuth.currentClientId, moduleNavigation.trialId, selectedAnalysisId],
		queryFn: () => getHeatmapAnalysisResults(),
		select: (data) => data.data as any[],
		enabled: !!moduleNavigation.trialId && !!userAuth.currentClientId && !!selectedAnalysisId,
	});

	const { isFetching: analysisOptionsDataLoading, data: analysisOptionsData } = useQuery({
		queryKey: ["mfa viewer heatmap options", userAuth.currentClientId, moduleNavigation.trialId],
		queryFn: () => getAnalysisOptions(),
		select: (data) => data.data as AnalysisOptionsData,
		enabled: !!moduleNavigation.trialId && !!userAuth.currentClientId,
	});

	const analysisOptions = useMemo(() => {
		if (analysisOptionsData) {
			return setupAnalysisOptions(analysisOptionsData);
		}
		return [];
	}, [analysisOptionsData]);

	//-- When switching to timecourse, swap to NDVI or SNDVI with most data
	useEffect(() => {
		if (chartType === "line" && prevChartTypeRef.current !== "line") {
			const higherAnalysisCount = _.chain(analysisOptionsData?.analysisOptions)
				.filter((x) => x.analysisName.includes("NDVI"))
				.maxBy((x) => x.instanceCount)
				.value();

			//-- Find matching analysis option
			if (higherAnalysisCount?.analysisId) {
				const matchingOption = _.find(analysisOptions, (x) => x.text === higherAnalysisCount.analysisName);

				if (matchingOption && matchingOption.key !== selectedAnalysisId) {
					setSelectedAnalysisId(matchingOption.value as string);
				}
			}
		}
		prevChartTypeRef.current = chartType;
	}, [chartType, analysisOptionsData, analysisOptions, selectedAnalysisId]);

	const mappedDates = useMemo(() => {
		// case when there are dates
		if (heatmapData?.[0]?.dateCollected && selectedAnalysisId) {
			const mappedDates = _.reduce<HeatmapData, { [timeValue: number]: { dateCollected?: string; text: string } }>(
				_.uniqBy<HeatmapData>(heatmapData, "dateCollected"),
				(acc, hd, i) => {
					const date = DateTime.fromISO(hd.dateCollected!);
					return {
						...acc,
						[date.toMillis() + i]: {
							dateCollected: hd.dateCollected,
							text: date.toLocal().toFormat("LL/dd/yy"),
						},
					};
				},
				{},
			);
			return mappedDates;
		}

		// case when there are no dates but there is data (null vs undefined)
		if (heatmapData?.[0]?.dateCollected === null) {
			return {};
		}

		// case when data doesn't exist
		return null;
	}, [selectedAnalysisId, heatmapData]);

	const dateOptions = useMemo(() => {
		if (_.keys(mappedDates ?? {}).length !== 0) {
			let dateOpts = _.map(_.entries(mappedDates!), ([timeValue, hd]) => {
				return {
					key: timeValue,
					value: timeValue,
					text: hd.text,
				};
			});
			dateOpts = _.sortBy(dateOpts, [(opt) => Number(opt.key)], ["asc"]);
			return dateOpts;
		}
		return null;
	}, [mappedDates]);

	useEffect(() => {
		if (analysisOptions) {
			setSelectedAnalysisId((prev) => {
				if (!prev) {
					return (
						GEOMFA_MENU_HEATMAP.reduce<string | undefined>(
							(acc, text) => acc ?? _.find(analysisOptions, { text })?.key,
							undefined,
						) ?? analysisOptions[1]?.key
					);
				}
			});
		}
	}, [analysisOptions]);

	useEffect(() => {
		setSelectedTimeValue(0);
		if (_.keys(mappedDates).length !== 0) {
			const orderedPairedFlights = _.chain(mappedDates)
				.entries()
				.orderBy(([timeValue, _]) => Number(timeValue) || Infinity, ["desc"])
				.uniq()
				.value();

			setSelectedTimeValue(Number(orderedPairedFlights?.[0][0]));
		}

		if (mappedDates) {
			const text = analysisOptions.find((o) => o.key === selectedAnalysisId).text;
			updateAxis({ y: text });
		}
	}, [mappedDates, selectedAnalysisId, analysisOptions]);

	useEffect(() => {
		updateHeatMapDataLoading(heatmapDataLoading);
	}, [heatmapDataLoading]);

	useEffect(() => {
		if (heatmapData && selectedAnalysisId) {
			updateHeatMapDataAllDates(heatmapData);
		}
	}, [heatmapData, selectedAnalysisId]);

	useEffect(() => {
		if (
			heatmapData &&
			selectedAnalysisId &&
			(mappedDates?.[selectedTimeValue] || heatmapData[0]?.dateCollected === null)
		) {
			const selectedHeatmapFlightDate = mappedDates?.[selectedTimeValue]?.dateCollected;
			const filteredHeatmapData = heatmapData[0]?.dateCollected
				? _.filter(heatmapData, { dateCollected: selectedHeatmapFlightDate })
				: heatmapData;
			updateHeatMapData(filteredHeatmapData);
		} else if (heatmapData && selectedAnalysisId) {
			updateHeatMapData(heatmapData);
		}
	}, [heatmapData, selectedAnalysisId, mappedDates, selectedTimeValue]);

	return (
		<div ref={setNodeRef} className="layerBlock" style={style} {...attributes}>
			<div ref={setActivatorNodeRef} className="layerBlockBar" {...listeners}>
				<Icon className="drag-handle-vertical" />
			</div>
			<Icon className="layerBlockIcon map" size="large" />
			<span className="layerBlockLabel">
				{minimized ? analysisOptions.find((o) => o.value === selectedAnalysisId)?.text : "Heatmap"}
			</span>
			<div className="layerBlockButtons">
				<Button
					className="actionsPopupTrigger iconButton"
					icon
					onClick={() => {
						updateHeatmapVisible(!heatmapVisibleLocal);
						setHeatmapVisibleLocal((prev) => !prev);
					}}
				>
					<Icon className={`eye${heatmapVisibleLocal ? "" : " closed"}`} size="large" />
				</Button>
				<HeatmapOptions />
			</div>
			<Select
				className="layerBlockSelect"
				fluid
				loading={analysisOptionsDataLoading || heatmapDataLoading}
				options={analysisOptions}
				value={selectedAnalysisId}
				onChange={(_, data) => {
					setSelectedAnalysisId(data.value as string);
				}}
			/>
			{(dateOptions !== null || heatmapDataLoading) && dateOptions && chartType !== "line" && (
				<>
					<DateTimeSlider
						className="layerBlockDateSlider"
						mappedDates={mappedDates!}
						onChange={(date) => {
							if (mappedDates![date]) {
								setSelectedTimeValue(date);
							}
						}}
						selectedDate={selectedTimeValue}
					/>
					<Select
						className="layerBlockDatePicker"
						loading={heatmapDataLoading}
						compact
						icon
						disabled={dateOptions === null}
						options={dateOptions ?? []}
						value={selectedTimeValue.toString()}
						onChange={(_, data) => {
							setSelectedTimeValue(Number(data.value));
						}}
					/>
				</>
			)}
		</div>
	);
};
